import { Dispatch, FC, SetStateAction, useRef, useState } from 'react';
import styled from '@emotion/styled';
import { useMutation } from '@apollo/client';
import { DateTime } from 'luxon';
import { Cancel, Clear } from '@material-ui/icons';
import MoreHorizIcon from '@material-ui/icons/MoreHoriz';
import DescriptionIcon from '@material-ui/icons/Description';
import {
  Backdrop,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Drawer,
  Grid,
  IconButton,
  Menu,
  MenuItem,
  TextField,
  Typography,
  withStyles,
} from '@material-ui/core';

import { Container as DrawerContainer, Footer as DrawerFooter } from '../Drawer';
import {
  COLOR_BORDER,
  COLOR_BUTTON_BACKGROUND,
  COLOR_BUTTON_TEXT,
  COLOR_DISABLE_BACKGROUND,
  COLOR_EFFECT,
  COLOR_TEXT_DISABLE,
  COLOR_TEXT_MAIN,
} from '../../../../theme/colors';
import LoadingButton from '../../primitive/LoadingButton';
import {
  AttachableAttachment,
  AttachableAttachmentWrapper,
  DrawerThreadHeader,
  HeaderAvater,
  HeaderDate,
  HeaderUserName,
  Paragraph,
  ThreadImageWrapper,
  ThreadItemWrapper,
} from '../Experiments/Thread/ThreadComponents';
import globalState from '../../../../operations/commands';
import {
  DeleteTopicCommentDocument,
  DeleteTopicCommentMutation,
  DeleteTopicCommentMutationVariables,
  PostTopicCommentDocument,
  PostTopicCommentMutation,
  PostTopicCommentMutationVariables,
  Topic,
} from '../../../../generated/graphql';
import Loading from '../Loading';
import { useUploadAttachmentFile } from '../../../../hooks/useUploadAttachmentFile';
import { EditCommentDrawer } from './EditCommentDrawer';
import Config from '../../../../core/Config';

type Props = {
  refetch: () => void;
  topic: Topic;
  open: boolean;
  setOpen: Dispatch<SetStateAction<boolean>>;
  loadingTopics: boolean;
};

const MAX_LENGTH = 4000;

/**
 * ファイルを追加する
 */
export const ThreadDrawer: FC<Props> = ({ refetch, topic, open, setOpen, loadingTopics }) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const [files, setFiles] = useState<File[]>([]);
  const [urls, setUrls] = useState<string[]>([]);
  const [message, setMessage] = useState('');
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const openMenu = Boolean(anchorEl);
  const [openDelete, setOpenDelete] = useState<boolean>(false);
  const [commentId, setCommentId] = useState<string>('');
  const [commentIndex, setCommentIndex] = useState<number>(-1);
  const [openEditThreadComment, setOpenEditThreadComment] = useState<boolean>(false);
  const config: Config = (window as any).__hugotconfig__;

  const { uploadFile } = useUploadAttachmentFile('comments');

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleModalOpen = () => {
    setOpenDelete(true);
  };

  const handleModalClose = () => {
    setOpenDelete(false);
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e?.target?.files) {
      if (50 * 1000 ** 2 < e?.target?.files[0].size) {
        globalState.setErrors({
          message: 'ファイルの最大サイズを超えています。',
          name: 'UnimportantError',
          context: 'planbcd',
        });
        return;
      }

      setFiles([...files, e.target.files[0]]);

      const newUrls = [...urls];
      newUrls.push(URL.createObjectURL(e.target.files[0]));
      setUrls(newUrls);
    }
  };

  const deleteFile = (index: number) => {
    const newFiles = [...files];
    const newUrls = [...urls];

    newFiles.splice(index, 1);
    newUrls.splice(index, 1);
    setFiles(newFiles);
    setUrls(newUrls);
  };

  const [postComment, { loading: loadingPost }] = useMutation<
    PostTopicCommentMutation,
    PostTopicCommentMutationVariables
  >(PostTopicCommentDocument, {
    variables: {
      input: {
        topic_id: topic.id,
        project_topic_comment: {
          comment: message,
        },
      },
    },
    context: { clientName: 'planbcd' },
    onCompleted: (data: PostTopicCommentMutation): void => {
      files.map((file) => {
        uploadFile(file, data.postTopicComment.id);
      });
      refetch();
      setFiles([]);
      setUrls([]);
      setMessage('');
      setTimeout(() => {
        refetch();
      }, 2000);
    },
    onError: () => {
      globalState.setErrors({
        message: 'error',
        name: 'NonFatalError',
        context: { clientName: 'search_console' },
      });
    },
  });

  const [deleteComment, { loading: loadingDelete }] = useMutation<
    DeleteTopicCommentMutation,
    DeleteTopicCommentMutationVariables
  >(DeleteTopicCommentDocument, {
    variables: {
      input: {
        id: commentId,
      },
    },
    context: { clientName: 'planbcd' },
    onCompleted: () => {
      refetch();
      setCommentId('');
    },
    onError: (error) => {
      console.error(error);
      globalState.setErrors({
        message: 'error',
        name: 'NonFatalError',
        context: { clientName: 'planbcd' },
      });
    },
  });

  return (
    <>
      <Backdrop open={loadingTopics || loadingDelete || loadingPost} style={{ zIndex: 20000, color: '#fff' }}>
        <CircularProgress color="inherit" />
      </Backdrop>
      {topic.comments && topic.comments[commentIndex] ? (
        <EditCommentDrawer
          refetch={refetch}
          open={openEditThreadComment}
          setOpen={setOpenEditThreadComment}
          selectedComment={topic.comments[commentIndex]}
        />
      ) : null}
      <Dialog open={openDelete} onClose={handleModalClose}>
        <DialogTitle>コメントの削除</DialogTitle>
        <DialogContent>
          <DialogContentText>コメントを削除します。</DialogContentText>
          <DialogContentText>コメントを削除するともとに戻すことができません。</DialogContentText>
          <DialogContentText>よろしければ次にお進みください。</DialogContentText>
        </DialogContent>
        <DialogActions>
          <SecondaryButton
            disabled={false}
            onClick={() => {
              handleModalClose();
            }}
          >
            キャンセル
          </SecondaryButton>
          <PrimaryButton
            disabled={false}
            onClick={() => {
              deleteComment();
              handleModalClose();
            }}
          >
            OK
          </PrimaryButton>
        </DialogActions>
      </Dialog>
      <Drawer
        anchor="right"
        open={open}
        onClose={(_, reason: any) => {
          if (reason !== 'backdropClick') {
            reason;
          }
        }}
      >
        <Menu
          id="long-menu"
          anchorEl={anchorEl}
          keepMounted
          open={openMenu}
          onClose={handleClose}
          PaperProps={{
            style: {
              maxHeight: 48 * 4.5,
              width: '20ch',
            },
          }}
        >
          <MenuItem
            disabled={topic.comments ? !topic.comments[commentIndex]?.permissions.update.permit : true}
            onClick={() => {
              setOpenEditThreadComment(true);
              handleClose();
            }}
          >
            編集
          </MenuItem>
          <MenuItem
            disabled={topic.comments ? !topic.comments[commentIndex]?.permissions.destroy.permit : true}
            onClick={() => {
              handleModalOpen();
              handleClose();
            }}
          >
            削除
          </MenuItem>
        </Menu>
        <DrawerContainer>
          <DrawerContent style={{ height: files.length > 0 ? 'calc(100% - 356px)' : 'calc(100% - 176px)' }}>
            <Header>
              <IconButton onClick={() => setOpen(false)}>
                <Clear />
              </IconButton>
              <Title>{topic.title}</Title>
            </Header>
            <Content>
              <Grid container spacing={6}>
                <Grid item xs={12}>
                  <DrawerThreadHeader>
                    <TopicInfoWrapper>
                      <HeaderAvater src={topic.user.avatar_image_url} />
                      <HeaderUserName
                        href={`${config.K2_BASE_URL}/users/${topic.user.id}`}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        {topic.user.username}
                      </HeaderUserName>
                      <HeaderDate>{DateTime.fromISO(topic.created_at).toFormat('yyyy/MM/dd hh:mm')}</HeaderDate>
                    </TopicInfoWrapper>
                  </DrawerThreadHeader>
                  <ThreadItemWrapper>
                    <Paragraph>{topic.comment}</Paragraph>
                    <ThreadImageWrapper>
                      {topic.attachables_attachments ? (
                        <ThreadImageWrapper>
                          {topic.attachables_attachments.map((data) =>
                            data?.attachment.content_type === 'image/jpeg' ||
                            data?.attachment.content_type === 'image/png' ? (
                              <AttachableAttachmentWrapper>
                                <AttachableAttachment
                                  href={data?.attachment.urls.original}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                >
                                  <img
                                    key={data?.id}
                                    style={{ maxHeight: 180 }}
                                    alt="thread_img"
                                    src={data?.attachment.urls.original}
                                  ></img>
                                </AttachableAttachment>
                              </AttachableAttachmentWrapper>
                            ) : (
                              <AttachableAttachmentWrapper>
                                <DescriptionIcon />
                                <AttachableAttachment
                                  href={data?.attachment.urls.original}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                >
                                  {data?.attachment.name}
                                </AttachableAttachment>
                              </AttachableAttachmentWrapper>
                            ),
                          )}
                        </ThreadImageWrapper>
                      ) : null}
                    </ThreadImageWrapper>
                  </ThreadItemWrapper>
                </Grid>
                {loadingTopics ? (
                  <Loading />
                ) : topic.comments ? (
                  topic.comments.map((comment, index) => (
                    <Grid key={comment?.id} item xs={12}>
                      <DrawerThreadHeader>
                        <TopicInfoWrapper>
                          <HeaderAvater src={comment?.user.avatar_image_url} />
                          <HeaderUserName
                            href={`${config.K2_BASE_URL}/users/${comment?.user.id}`}
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            {comment?.user.username}
                          </HeaderUserName>
                          <HeaderDate>
                            {comment ? DateTime.fromISO(comment.updated_at).toFormat('yyyy/MM/dd hh:mm') : null}
                          </HeaderDate>
                        </TopicInfoWrapper>
                        <IconButton
                          aria-label="more"
                          aria-controls="long-menu"
                          aria-haspopup="true"
                          onClick={(e) => {
                            comment && setCommentId(comment.id);
                            setCommentIndex(index);
                            handleClick(e);
                          }}
                        >
                          <MoreHorizIcon />
                        </IconButton>
                      </DrawerThreadHeader>
                      <ThreadItemWrapper>
                        <Paragraph>{comment?.comment}</Paragraph>
                        {comment?.attachables_attachments ? (
                          <ThreadImageWrapper>
                            {comment?.attachables_attachments.map((data) =>
                              data?.attachment.content_type === 'image/jpeg' ||
                              data?.attachment.content_type === 'image/png' ? (
                                <AttachableAttachmentWrapper>
                                  <AttachableAttachment
                                    href={data?.attachment.urls.original}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                  >
                                    <img
                                      key={data?.id}
                                      style={{ maxHeight: 180 }}
                                      alt="thread_img"
                                      src={data?.attachment.urls.original}
                                    ></img>
                                  </AttachableAttachment>
                                </AttachableAttachmentWrapper>
                              ) : (
                                <AttachableAttachmentWrapper>
                                  <DescriptionIcon />
                                  <AttachableAttachment
                                    href={data?.attachment.urls.original}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                  >
                                    {data?.attachment.name}
                                  </AttachableAttachment>
                                </AttachableAttachmentWrapper>
                              ),
                            )}
                          </ThreadImageWrapper>
                        ) : null}
                      </ThreadItemWrapper>
                    </Grid>
                  ))
                ) : null}
              </Grid>
            </Content>
          </DrawerContent>
          <DrawerFooter>
            <div>
              <StyledInputField
                variant={`outlined`}
                maxRows={4}
                value={message}
                onChange={(e) => {
                  if (e.target.value.length > MAX_LENGTH) {
                    return;
                  }
                  setMessage(e.target.value);
                }}
                disabled={!topic.permissions.can_comment}
                multiline
                placeholder="メッセージを入力してください"
              />
              <ThreadMessageLabel>{`${message.length} / ${MAX_LENGTH}`}</ThreadMessageLabel>
            </div>
            <FileUploadWrapper>
              {files.length > 0 && urls.length > 0
                ? urls.map((url, index) =>
                    files[index].type === 'image/jpeg' || files[index].type === 'image/png' ? (
                      <UploadedImageFileWrapper key={url}>
                        <UploadedFile src={url} alt={files[index].name} />
                        <UploadedImageFileDeleteButton
                          onClick={() => {
                            deleteFile(index);
                          }}
                        >
                          <Cancel />
                        </UploadedImageFileDeleteButton>
                      </UploadedImageFileWrapper>
                    ) : (
                      <UploadedFileWrapper key={url}>
                        <div>{files[index].name}</div>
                        <UploadedFileDeleteButton
                          onClick={() => {
                            deleteFile(index);
                          }}
                        >
                          <Cancel />
                        </UploadedFileDeleteButton>
                      </UploadedFileWrapper>
                    ),
                  )
                : null}
            </FileUploadWrapper>
            <Borderline />
            <FooterWrapper>
              <FooterFileUploadWrapper>
                <SecondaryButton
                  disabled={files.length >= 3 || !topic.permissions.can_comment.permit}
                  onClick={() => {
                    if (inputRef?.current) {
                      inputRef.current.click();
                    }
                  }}
                >
                  アップロードする素材・資料を選択する
                </SecondaryButton>
                <FooterFileUploadLabel>{'最大50MB / 最大3ファイル'}</FooterFileUploadLabel>
              </FooterFileUploadWrapper>
              <PrimaryButton
                loading={false}
                disabled={message.length === 0 || !topic.permissions.can_comment.permit}
                onClick={() => {
                  postComment();
                }}
              >
                送信する
              </PrimaryButton>
            </FooterWrapper>
            <input
              hidden
              id="img"
              type="file"
              ref={inputRef}
              accept="*/*"
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleChange(e)}
            />
          </DrawerFooter>
        </DrawerContainer>
      </Drawer>
    </>
  );
};

const Header = styled('div')(`
  display: flex;
  align-items: center;
  margin-bottom: 12px;
`);

const Content = styled('div')(`
  height: calc(100% - 356px);
  width: 100%;
  padding-top: 24px;
`);

const Title = styled(Typography)(`
  font-weight: 700;
  font-size: 24px;
  color: ${COLOR_TEXT_MAIN};
  overflow-wrap: anywhere;
`);

const PrimaryButton = styled(LoadingButton)(`
  color: ${COLOR_BUTTON_TEXT};
  height: 40px;
  font-size: 14px;
  background: ${COLOR_TEXT_MAIN};
  font-weight: 700;
  border-color: ${COLOR_TEXT_MAIN};
  border-radius: 4px;
  padding: 12px 24px;
  text-transform: none;
  :hover {
    background: ${COLOR_EFFECT};
    border-color: ${COLOR_EFFECT};
  }
  :disabled {
    background: ${COLOR_DISABLE_BACKGROUND};
    color: ${COLOR_TEXT_DISABLE};
    border: ${COLOR_TEXT_DISABLE};
  }
`);

const SecondaryButton = styled(Button)<{ width?: string }>(`
  color: ${COLOR_TEXT_MAIN};
  height: 40px;
  padding: 13px 16px;
  font-size: 14px;
  background: ${COLOR_BUTTON_BACKGROUND};
  font-weight: 700;
  border: 1.5px solid ${COLOR_BORDER};
  border-radius: 4px;
  margin-right: 16px;
  :hover {
    background: ${COLOR_BUTTON_BACKGROUND};
    color: ${COLOR_EFFECT};
  }
`);

const DrawerContent = styled('div')(`
  padding: 40px 40px;
  width: 864px;
  position: relative;
  overflow-y: auto;
  fieldset {
    outline: none;
    border: 1.5px solid ${COLOR_BORDER};
  }
  .MuiOutlinedInput-root:hover fieldset {
    outline: none;
    border: 1.5px solid ${COLOR_BORDER};
  }
  .MuiOutlinedInput-root.Mui-focused {
    fieldset {
      outline: none;
      border: 1.5px solid ${COLOR_EFFECT};
    }
  }
`);

const StyledInputField = withStyles({
  root: {
    width: '100%',
    borderRadius: '8px',
    background: '#FFFFFF',
    '& .MuiOutlinedInput-root': {
      color: COLOR_TEXT_MAIN,
      borderRadius: '8px',
      fontSize: '14px',
      '& fieldset': {},
      '&.Mui-error fieldset': {
        border: '1.5px solid #FE7877',
      },
      '&.Mui-error input': {
        color: '#FE7877',
      },
    },
  },
})(TextField);

const ThreadMessageLabel = styled(Typography)`
  color: #4c566c;
  font-size: 10px;
  margin-top: 8px;
  display: flex;
  justify-content: flex-end;
`;

const FileUploadWrapper = styled('div')`
  display: flex;
`;

const UploadedImageFileWrapper = styled('div')`
  display: flex;
  align-items: flex-start;
  margin-right: 24px;
`;

const UploadedFileWrapper = styled('div')`
  display: flex;
  margin-bottom: 16px;
  align-items: center;
`;

const UploadedFile = styled('img')`
  max-height: 180px;
  max-width: 240px;
  border-radius: 4px;
  box-shadow: 0px 1px 9px rgb(0 0 0 / 15%);
`;

const UploadedImageFileDeleteButton = styled(IconButton)`
  margin-left: -48px;
`;

const UploadedFileDeleteButton = styled(IconButton)``;

const Borderline = styled('div')(`
  height: 1px;
  border: 0.5px solid ${COLOR_BORDER};
  width: 100%;
  margin: 16px 0;
`);

const FooterWrapper = styled('div')`
  display: flex;
  justify-content: space-between;
`;

const FooterFileUploadWrapper = styled('div')`
  display: flex;
  align-items: center;
`;

const FooterFileUploadLabel = styled(Typography)`
  font-size: 12px;
  color: #4c566c;
`;

const TopicInfoWrapper = styled('div')`
  display: flex;
  align-items: center;
`;
